<template>
    <el-dialog
        title="Create Job Order"
        :visible.sync="visible"
        :close-on-click-modal="false"
        :append-to-body="true"
        width="850px"
    >
        <ValidationObserver ref="observer" v-slot="{ passes }">
            <div class="small mb-3">
                <strong>Source Transaction:</strong> {{ transaction && (transaction.series || transaction.id) }}<br>
                <strong>Requested Completion Date:</strong> {{ transaction && transaction.requested_completion_date || '-' }}
            </div>
            <div v-if="loadingItems" class="text-center py-4">
                <span class="spinner-border spinner-border-sm mr-2" /> Loading available source quantities...
            </div>
            <el-table v-else :data="eligibleItems" border size="small" max-height="280" empty-text="No flagged source items found.">
                <el-table-column prop="code" label="Code" width="140" />
                <el-table-column label="Photo" width="82" align="center">
                    <template slot-scope="scope">
                        <img
                            :src="scope.row.photo || defaultItemPhoto"
                            alt="Item photo"
                            style="width: 55px; height: 55px; object-fit: contain"
                            @error="onPhotoError"
                        />
                    </template>
                </el-table-column>
                <el-table-column prop="description" label="Description" min-width="180" />
                <el-table-column prop="source_quantity" label="Invoice Qty" width="125" align="right" />
                <el-table-column prop="allocated_quantity" label="Allocated to Other Job Orders" width="220" align="right" />
                <el-table-column prop="available_quantity" label="Max Allocatable Qty" width="170" align="right" />
                <el-table-column label="Job Order Qty" width="155" align="center">
                    <template slot-scope="scope">
                        <el-input-number
                            v-model="scope.row.allocate_quantity"
                            :min="0"
                            :max="Number(scope.row.available_quantity)"
                            :precision="4"
                            :step="1"
                            :disabled="Number(scope.row.available_quantity) <= 0 || saving"
                            size="mini"
                            @change="clearAllocationValidation(scope.row)"
                        />
                        <div v-if="allocationErrors[String(scope.row.transaction_item_id)]" class="text-danger small text-left">
                            {{ allocationErrors[String(scope.row.transaction_item_id)] }}
                        </div>
                    </template>
                </el-table-column>
            </el-table>
            <div v-if="allocationErrorMessage" class="text-danger small mt-2" role="alert">
                {{ allocationErrorMessage }}
            </div>
            <ThatsMultiSelect label="Priority" labelClass="col-sm-4" fieldClass="col-sm-8 pl-0" vid="priority" name="priority" v-model="form.priority" :options="priorities" :multiple="false" :reduce="option => option.value" :reduceLabel="'label'" :validated="validated" :rules="'required'" size="sm" />
            <ThatsDatePicker label="Committed Completion Date" labelClass="col-sm-4" fieldClass="col-sm-8 pl-0" vid="committed_completion_date" name="committed_completion_date" v-model="form.committed_completion_date" :validated="validated" :rules="''" size="sm" />
            <ThatsInput label="Subject" labelClass="col-sm-4" fieldClass="col-sm-8 pl-0" vid="subject" name="subject" v-model="form.subject" :validated="validated" :rules="''" size="sm" />
            <ThatsTextarea label="Description" labelClass="col-sm-4" fieldClass="col-sm-8 pl-0" vid="description" name="description" v-model="form.description" :validated="validated" :rules="''" size="sm" />
            <ThatsTextarea label="Remarks" labelClass="col-sm-4" fieldClass="col-sm-8 pl-0" vid="remarks" name="remarks" v-model="form.remarks" :validated="validated" :rules="''" size="sm" />
        </ValidationObserver>
        <div slot="footer" class="text-right">
            <mdb-btn color="elegant" size="sm" :disabled="saving || loadingItems || !hasAllocation" @click.native="submit">
                <span v-if="saving" class="spinner-border spinner-border-sm" /> {{ saving ? 'Creating...' : 'Create' }}
            </mdb-btn>
            <mdb-btn color="grey" size="sm" @click.native="closeDialog">Close</mdb-btn>
        </div>
    </el-dialog>
</template>

<script>
import { mdbBtn } from 'mdbvue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { ThatsDatePicker, ThatsInput, ThatsTextarea, ThatsMultiSelect } from '../../components/thats/Thats'

export default {
    components: { mdbBtn, ValidationObserver, ThatsDatePicker, ThatsInput, ThatsTextarea, ThatsMultiSelect },
    data() {
        return {
            visible: false,
            validated: false,
            transaction: null,
            form: {},
            sourceItems: [],
            allocationErrors: {},
            allocationErrorMessage: '',
            loadingItems: false,
            availabilityRequestToken: 0,
            defaultItemPhoto: 'https://media.thatsliving.com/assets/default-item-img-small.jpg',
            priorities: [
                { label: 'Low', value: 'low' },
                { label: 'Normal', value: 'normal' },
                { label: 'High', value: 'high' },
                { label: 'Urgent', value: 'urgent' },
            ],
        }
    },
    computed: {
        ...mapGetters('JobOrderModule', ['saving']),
        eligibleItems() { return this.sourceItems },
        hasAllocation() { return this.sourceItems.some(item => Number(item.allocate_quantity || 0) > 0) },
    },
    methods: {
        ...mapActions('JobOrderModule', ['createFromTransaction', 'getAvailableItems']),
        onPhotoError(event) {
            if (event.target.src !== this.defaultItemPhoto) event.target.src = this.defaultItemPhoto
        },
        async openDialog(transaction) {
            this.transaction = transaction
            this.form = {
                entity_id: this.sessionEntityId(),
                transaction_id: transaction.mdbID || transaction.id,
                document_date: transaction.date || null,
                committed_completion_date: null,
                priority: 'normal',
                subject: null,
                description: null,
                remarks: null
            }
            this.sourceItems = []
            this.clearAllocationValidation()
            this.validated = false
            this.visible = true
            this.loadingItems = true
            const requestToken = ++this.availabilityRequestToken
            try {
                const items = await this.getAvailableItems({ transaction_id: this.form.transaction_id })
                if (requestToken === this.availabilityRequestToken) {
                    this.sourceItems = items.map(item => Object.assign({}, item, {
                        allocate_quantity: Number(item.available_quantity || 0),
                    }))
                }
            } catch (error) {
                const data = error.response && error.response.data
                if (requestToken === this.availabilityRequestToken) {
                    this.$ThatsNotify.error({ message: data && data.message ? data.message : 'Unable to load available source quantities' })
                }
            } finally {
                if (requestToken === this.availabilityRequestToken) this.loadingItems = false
            }
        },
        sessionEntityId() {
            const user = JSON.parse(localStorage.getItem('user') || '{}')
            return user.location ? user.location.id : null
        },
        closeDialog() {
            this.availabilityRequestToken += 1
            this.loadingItems = false
            this.clearAllocationValidation()
            this.visible = false
        },
        async submit() {
            if (this.loadingItems || this.saving) return
            this.validated = true
            const valid = await this.$refs.observer.validate()
            if (!valid) return
            const items = this.sourceItems
                .filter(item => Number(item.allocate_quantity || 0) > 0)
                .map(item => ({ transaction_item_id: item.transaction_item_id, quantity: Number(item.allocate_quantity) }))
            if (!items.length) {
                this.allocationErrorMessage = 'Allocate at least one available item quantity.'
                this.$ThatsNotify.error({ message: 'Allocate at least one available item quantity.' })
                return
            }
            this.createFromTransaction(Object.assign({}, this.form, { items })).then(record => {
                this.$ThatsNotify.success({ message: 'Job Order created successfully' })
                this.$emit('success', record)
                this.closeDialog()
            }).catch(error => {
                const data = error.response && error.response.data
                if (data && data.errors && this.$refs.observer) this.$refs.observer.setErrors(data.errors)
                this.setAllocationValidationErrors(data && data.errors, items)
                this.$ThatsNotify.error({ message: data && data.message ? data.message : 'Unable to create Job Order' })
            })
        },
        setAllocationValidationErrors(errors, submittedItems) {
            this.clearAllocationValidation()
            if (!errors) return
            const generalErrors = errors.items || []
            this.allocationErrorMessage = Array.isArray(generalErrors) ? generalErrors.join(' ') : String(generalErrors)
            Object.keys(errors).forEach(field => {
                const match = field.match(/^items\.(\d+)(?:\.|$)/)
                if (!match) return
                const item = submittedItems[Number(match[1])]
                if (!item) return
                const messages = errors[field]
                this.$set(
                    this.allocationErrors,
                    String(item.transaction_item_id),
                    Array.isArray(messages) ? messages.join(' ') : String(messages)
                )
            })
        },
        clearAllocationValidation(item) {
            if (item) this.$delete(this.allocationErrors, String(item.transaction_item_id))
            else this.allocationErrors = {}
            this.allocationErrorMessage = ''
        }
    }
}
</script>
